import React from 'react';
import moment from 'moment';
import {Button} from 'react-bootstrap';
import {Link} from 'react-router-dom';
import {connect, getRouter, RenderUtils, SwList, SwPortlet, WorkflowUtils} from 'simplifyworks-core-ui';
import i18n from 'i18next';
import {organOfferIdByEnv} from '../../domain/constants';

const DEFAULT_ORDERING = [
	{ property: 'created', order: 'desc' },
	{ property: 'id', order: 'asc', system: true }
];

const getEntryDetailPath = (entryId, definitionId) => (
	definitionId
		? '/ood/entries/' + entryId + '/definition/' + definitionId
		: '/ood/entries/' + entryId
);

const getCodebookLabel = (value) => (
	value?.codebookLabel || value?.name || value?.abbreviation || null
);

const getOrganizationLabel = (value) => (
	value?.organization?.name || value?.name || getCodebookLabel(value)
);

const getItemsLabel = (value) => (
	value?.items
		?.map(item => getCodebookLabel(item))
		?.filter(Boolean)
		?.join(', ') || null
);

const getOrganLabel = (entry) => (
	getCodebookLabel(entry?.attributes?.ORG_DECLINED)
	|| getItemsLabel(entry?.attributes?.ORGANS)
	|| getCodebookLabel(entry?.attributes?.ORGAN)
	|| entry?.name
	|| i18n.t('foedus-temporary-user-organ-offers-untitled')
);

const getCountryLabel = (entry) => (
	getOrganizationLabel(entry?.attributes?.COUNTRY)
	|| getOrganizationLabel(entry?.attributes?.COUNTRY_TRANS)
	|| null
);

const getStateLabel = (stateCb) => {
	if (!stateCb) {
		return null;
	}

	const stateName = RenderUtils.renderMultilangProperty(stateCb, 'name');
	if (stateName && stateCb?.code) {
		return stateName + ' (' + stateCb.code + ')';
	}

	return stateName || stateCb?.code || stateCb?.codebookLabel || null;
};

const renderEntryStateBadge = (stateCb) => {
	const label = getStateLabel(stateCb);

	if (!label) {
		return null;
	}

	return WorkflowUtils.renderStateBadge({
		label,
		variant: stateCb?.fieldValuesMap?.color || 'secondary'
	});
};

const handleRowKeyDown = (event, detailPath) => {
	if (event.key === 'Enter' || event.key === ' ') {
		event.preventDefault();
		getRouter().navigate(detailPath);
	}
};

const renderEntryRow = (entry, definitionId) => {
	const detailPath = getEntryDetailPath(entry?.id, definitionId);
	const countryLabel = getCountryLabel(entry);

	return (
		<div
			role='button'
			tabIndex={0}
			onClick={() => getRouter().navigate(detailPath)}
			onKeyDown={(event) => handleRowKeyDown(event, detailPath)}
			style={{
				cursor: 'pointer',
				padding: '8px 4px'
			}}
		>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'flex-start',
					gap: '12px'
				}}
			>
				<div style={{minWidth: 0}}>
					<div style={{marginBottom: '4px'}}>
						<Link
							to={detailPath}
							onClick={(event) => event.stopPropagation()}
							style={{fontWeight: 600}}
						>
							{entry?.identifier || '#' + entry?.id}
						</Link>
					</div>

					<div style={{fontSize: '14px', marginBottom: countryLabel ? '4px' : 0}}>
						{getOrganLabel(entry)}
					</div>

					{countryLabel ? (
						<div style={{fontSize: '12px', color: '#6c757d'}}>
							{countryLabel}
						</div>
					) : null}
				</div>

				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'flex-end',
						gap: '8px',
						flexShrink: 0
					}}
				>
					<div>{renderEntryStateBadge(entry?.stateCb)}</div>

					<div style={{fontSize: '12px', color: '#6c757d', textAlign: 'right'}}>
						{entry?.created
							? moment(entry.created).format(i18n.t('patternDateTime'))
							: '-'}
					</div>
				</div>
			</div>
		</div>
	);
};

const FoedusTemporaryUserOrganOffersPortlet = (props) => {
	const { isTemporaryUser } = props;
	const definitionId = organOfferIdByEnv[globalParams.hostName];
	const listName = 'foedusTemporaryUserOrganOffersPortlet-' + definitionId;

	const openAll = () => {
		getRouter().navigate('/ood/entries-by-definition/' + definitionId);
	};

	if (!isTemporaryUser || !definitionId) {
		return null;
	}

	return (
		<SwPortlet
			parentProps={props}
			render={() => (
				<div style={{padding: '4px 0'}}>
					<div style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						gap: '12px',
						marginBottom: '12px'
					}}>
						<div>
							<div style={{fontWeight: 600, fontSize: '16px'}}>
								{i18n.t('foedus-temporary-user-organ-offers-title')}
							</div>
							<div style={{fontSize: '13px', color: '#6c757d'}}>
								{i18n.t('foedus-temporary-user-organ-offers-description')}
							</div>
						</div>

						<Button variant='outline-primary' size='sm' onClick={openAll}>
							{i18n.t('foedus-temporary-user-organ-offers-open-all')}
						</Button>
					</div>

					<SwList
						name={listName}
						restPrefix='/api/ood/entries'
						removable={false}
						defaultOrdering={DEFAULT_ORDERING}
						fixedFilter={{
							definitionId: String(definitionId),
							filterTab: 'ALL',
							exclusiveIds: []
						}}
					>
						<SwList.Table
							hideHeader
							minWidth='320px'
							noData='foedus-temporary-user-organ-offers-empty'
						>
							<SwList.Custom
								property='all'
								orderable={false}
								render={(entry) => renderEntryRow(entry, definitionId)}
							/>
						</SwList.Table>

						<SwList.Info/>
						<SwList.Pagination defaultPageSize={5} pageSizes={[5, 10, 15]}/>
					</SwList>
				</div>
			)}
		/>
	);
};

const mapStateToProps = (state) => ({
	isTemporaryUser: state?.security?.tokenInfo?.currentPerson?.personalNumber === '9999999999'
});

export default connect(mapStateToProps)(FoedusTemporaryUserOrganOffersPortlet);
